<template>
  <a-input
    v-model:value="displayValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :size="size"
    :allow-clear="allowClear"
    :maxlength="19"
    :status="computedStatus"
    class="ant-credit-card-input"
    @update:value="onInput"
    @blur="onBlur"
    @pressEnter="onPressEnter"
  >
    <template #prefix>
      <i :class="brandIcon" />
    </template>
  </a-input>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import type { SizeType } from "ant-design-vue/es/config-provider";

interface Props {
  modelValue?: string;
  placeholder?: string;
  disabled?: boolean;
  size?: SizeType;
  allowClear?: boolean;
  status?: "" | "error" | "warning";
  validateOnBlur?: boolean;
  /** Emit raw digits */
  unmasked?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: "",
  placeholder: "ACCT-000003",
  disabled: false,
  size: "middle",
  allowClear: true,
  status: "",
  validateOnBlur: true,
  unmasked: true,
});

const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "change", value: string): void;
  (e: "blur", event: FocusEvent): void;
  (e: "pressEnter", event: KeyboardEvent): void;
  (e: "validate", valid: boolean): void;
  (e: "brand", brand: string): void;
}>();

const displayValue = ref("");

const onlyDigits = (value: string) => value.replace(/\D/g, "").slice(0, 16);

const formatCard = (digits: string) => digits.replace(/(.{4})/g, "$1 ").trim();

const detectBrand = (digits: string) => {
  if (/^4/.test(digits)) return "visa";
  if (/^5[1-5]/.test(digits) || /^2(2|7)/.test(digits)) return "mastercard";
  if (/^3[47]/.test(digits)) return "amex";
  if (/^6(011|5)/.test(digits)) return "discover";
  if (/^(62|81)/.test(digits)) return "unionpay";
  if (/^9704/.test(digits)) return "napas";
  return "card";
};

const brandIcon = computed(() => {
  const brand = detectBrand(onlyDigits(props.modelValue ?? ""));
  const map: Record<string, string> = {
    visa: "fab fa-cc-visa",
    mastercard: "fab fa-cc-mastercard",
    amex: "fab fa-cc-amex",
    discover: "fab fa-cc-discover",
    card: "fas fa-credit-card",
    napas: "fas fa-credit-card",
    unionpay: "fas fa-credit-card",
  };
  return map[brand] || map.card;
});

/** Luhn algorithm */
const isValidLuhn = (digits: string) => {
  if (!digits) return true;
  if (digits.length < 13 || digits.length > 16) return false;
  let sum = 0;
  let alt = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let n = Number(digits[i]);
    if (alt) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
    alt = !alt;
  }
  return sum % 10 === 0;
};

watch(
  () => props.modelValue,
  (val) => {
    const digits = onlyDigits(val ?? "");
    displayValue.value = formatCard(digits);
    emit("brand", detectBrand(digits));
  },
  { immediate: true }
);

const localError = computed(() => {
  if (!props.validateOnBlur) return false;
  const digits = onlyDigits(props.modelValue ?? "");
  return !!digits && !isValidLuhn(digits);
});

const computedStatus = computed(() => props.status || (localError.value ? "error" : ""));

const onInput = (val: string) => {
  const digits = onlyDigits(val);
  displayValue.value = formatCard(digits);
  const emitValue = props.unmasked ? digits : displayValue.value;
  emit("update:modelValue", emitValue);
  emit("change", emitValue);
  emit("brand", detectBrand(digits));
};

const onBlur = (e: FocusEvent) => {
  emit("validate", isValidLuhn(onlyDigits(props.modelValue ?? "")));
  emit("blur", e);
};

const onPressEnter = (e: KeyboardEvent) => emit("pressEnter", e);
</script>

<!--
  Hướng dẫn sử dụng — Credit Card
  Global: <h-creditCard v-model="card" @brand="b => {}" @validate="ok => {}" />
  Import: import { CreditCardInput } from '@/components/ant/input'

  Props: modelValue, unmasked, validateOnBlur...
  Events: update:modelValue, change, blur, pressEnter, validate, brand
  Docs: src/components/ant/input/README.md
-->
